// src/features/records/services/edges/geo.js

import { db } from '../../../../firebase';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

const GEO_BOUND_DECIMALS = 6;
const GEO_BOUND_ROUND_FACTOR = 10 ** GEO_BOUND_DECIMALS;

/**
 * Convert viewport bounds into Algolia-compatible bounding boxes.
 * Handles anti-meridian wrapping and clamps coordinates to valid ranges.
 */
export function mapBoundsToBoundingBoxes(bounds) {
  if (!bounds) return null;

  const { north, south, east, west } = bounds;
  const values = [north, south, east, west];
  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const clampLat = (lat) => Math.min(90, Math.max(-90, lat));
  const normaliseLng = (lng) => {
    if (!Number.isFinite(lng)) return NaN;
    let result = ((lng + 180) % 360 + 360) % 360 - 180;
    if (result === -180 && lng > 0) {
      result = 180;
    }
    return result;
  };
  const round = (value) => {
    if (!Number.isFinite(value)) return value;
    return Math.round(value * GEO_BOUND_ROUND_FACTOR) / GEO_BOUND_ROUND_FACTOR;
  };

  const top = round(Math.max(clampLat(north), clampLat(south)));
  const bottom = round(Math.min(clampLat(north), clampLat(south)));

  if (!Number.isFinite(top) || !Number.isFinite(bottom)) {
    return null;
  }

  const normalisedEastRaw = normaliseLng(east);
  const normalisedWestRaw = normaliseLng(west);

  if (!Number.isFinite(normalisedEastRaw) || !Number.isFinite(normalisedWestRaw)) {
    return null;
  }

  const LONGITUDE_EPSILON = 1e-9;
  const crossesAntimeridian = normalisedEastRaw < normalisedWestRaw - LONGITUDE_EPSILON;

  const normalisedEast = round(normalisedEastRaw);
  const normalisedWest = round(normalisedWestRaw);

  const latSpan = top - bottom;
  const lngSpan = crossesAntimeridian
    ? (180 - normalisedWestRaw) + (normalisedEastRaw - -180)
    : Math.abs(normalisedEastRaw - normalisedWestRaw);
  const rawLngSpan = Math.abs(east - west);
  if (latSpan >= 180 && (lngSpan >= 359.999 || rawLngSpan >= 359.999)) {
    return null;
  }

  if (crossesAntimeridian) {
    const boxes = [
      [top, normalisedWest, bottom, 180],
      [top, -180, bottom, normalisedEast]
    ].filter(([, westBound, , eastBound]) => Math.abs(eastBound - westBound) > 1e-6);

    return boxes.length > 0 ? boxes : null;
  }

  return [[top, Math.min(normalisedWest, normalisedEast), bottom, Math.max(normalisedWest, normalisedEast)]];
}

/**
 * Query edges within geospatial bounds (for MapView).
 */
export async function queryEdgesInBounds({ uid, bounds, perRangeLimit = 50, ownerIds }) {
  const { geohashQueryBounds } = await import('geofire-common');

  const toRad = (deg) => deg * Math.PI / 180;
  const normalizeLng = (lng) => {
    if (!Number.isFinite(lng)) return NaN;
    let result = ((lng + 180) % 360 + 360) % 360 - 180;
    if (result === -180 && lng > 0) {
      result = 180;
    }
    return result;
  };

  const lngSpanDeg = (() => {
    const east = normalizeLng(bounds.east);
    const west = normalizeLng(bounds.west);
    let span = east - west;
    if (span < 0) span += 360;

    const wrapsAntimeridian = bounds.east < bounds.west;

    if (!wrapsAntimeridian && span === 0) {
      return 360;
    }

    return wrapsAntimeridian ? Math.min(span, 360 - span) : span;
  })();

  const latSpanDeg = Math.abs(bounds.north - bounds.south);

  const centerLng = (() => {
    let east = normalizeLng(bounds.east);
    let west = normalizeLng(bounds.west);
    if (east < west) east += 360;
    let center = (east + west) / 2;
    if (center > 180) center -= 360;
    return center;
  })();
  const centerLat = (bounds.north + bounds.south) / 2;

  const kmPerDegLat = 110.574;
  const kmPerDegLng = 111.32 * Math.cos(toRad(centerLat));
  const latKm = latSpanDeg * kmPerDegLat;
  const lngKm = lngSpanDeg * Math.max(kmPerDegLng, 1e-6);
  const radiusKm = Math.max(0.5, 0.55 * Math.max(latKm, lngKm));

  const center = [centerLat, centerLng];
  const ranges = geohashQueryBounds(center, radiusKm * 1000);
  const baseCol = collection(db, `users/${uid}/accessibleRecords`);

  const runRange = async (start, end) => {
    const clauses = [
      where('lifecycle', '==', 'active'),
      where('viewFlags.hasLocation', '==', true),
      where('query.loc.geohash', '>=', start),
      where('query.loc.geohash', '<=', end),
      orderBy('query.loc.geohash'),
      orderBy('query.recordUpdatedAt', 'desc'),
      limit(perRangeLimit)
    ];

    if (ownerIds?.length) {
      clauses.splice(2, 0, where('ownerId', 'in', ownerIds.slice(0, 10)));
    }

    const q = query(baseCol, ...clauses);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  };

  const results = await Promise.all(ranges.map(([s, e]) => runRange(s, e)));

  const dedup = new Map();
  for (const arr of results) {
    for (const e of arr) {
      dedup.set(e.recordId, e);
    }
  }

  const boundingBoxes = mapBoundsToBoundingBoxes(bounds);
  const hasBoundingBoxes = Array.isArray(boundingBoxes) && boundingBoxes.length > 0;
  const pins = [];
  for (const e of dedup.values()) {
    const loc = e.query?.loc;
    if (loc?.lat == null || loc?.lng == null) continue;

    const lat = typeof loc.lat === 'number' ? loc.lat : Number(loc.lat);
    const lng = typeof loc.lng === 'number' ? loc.lng : Number(loc.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    let inBounds = false;

    if (hasBoundingBoxes) {
      const normalisedLng = normalizeLng(lng);
      if (Number.isFinite(normalisedLng)) {
        inBounds = boundingBoxes.some(([top, west, bottom, east]) => (
          lat <= top &&
          lat >= bottom &&
          normalisedLng >= west &&
          normalisedLng <= east
        ));
      }
    } else {
      inBounds = (
        lat >= bounds.south &&
        lat <= bounds.north &&
        lng >= bounds.west &&
        lng <= bounds.east
      );
    }

    if (inBounds) {
      pins.push({
        id: e.recordId,
        lat,
        lng,
        geohash: loc.geohash,
        preview: e.preview ?? {},
        query: e.query ?? {},
        ownerId: e.ownerId
      });
    }
  }

  return pins;
}
